import React, { useEffect, useRef } from "react";
import cx from "classnames";
import { useSpring, animated } from "@react-spring/web";

import styles from "./siteSettings.module.scss";

interface CardProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  headerRight?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
  expanded?: boolean;
  style?: React.CSSProperties;
}

const Card: React.FC<CardProps> = ({
  title,
  description,
  headerRight,
  className,
  children,
  expanded,
  style,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const [animation, api] = useSpring(() => ({
    height: 0,
    opacity: 0,
    config: { tension: 280, friction: 28 },
  }));

  useEffect(() => {
    if (expanded === undefined) return;
    const node = contentRef.current;
    if (!node) return;

    api.start({
      height: expanded ? node.scrollHeight : 0,
      opacity: expanded ? 1 : 0,
    });

    if (!expanded) return;

    const observer = new ResizeObserver(() => {
      api.start({ height: node.scrollHeight });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [expanded, api]);

  return (
    <section className={cx(styles.card, className)} style={style}>
      {(title || description || headerRight) && (
        <header className={styles.cardHeader}>
          <div className={styles.cardHeaderText}>
            {title && <div className={styles.cardTitle}>{title}</div>}
            {description && (
              <div className={styles.cardDescription}>{description}</div>
            )}
          </div>
          {headerRight && <div className={styles.cardHeaderRight}>{headerRight}</div>}
        </header>
      )}
      {expanded !== undefined ? (
        <animated.div className={styles.expandableWrapper} style={animation}>
          <div ref={contentRef} className={styles.expandableInner}>
            {children}
          </div>
        </animated.div>
      ) : (
        children
      )}
    </section>
  );
};

export default Card;
